/** @format */

import React from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom"; // For internal links
import { ExternalLink, Link as LinkIcon } from "lucide-react"; // For external link indicators

// --- Custom Media Renderer (Simplified & Safer) ---
// Renders images nicely, suggests using HTML for video/audio in Markdown source
const CustomMediaRenderer = ({ src, alt, title }) => {
	if (!src) return null;

	// Basic error handling for images
	const handleError = (e) => {
		e.target.style.display = "none"; // Hide broken image link
		// Optionally replace with a placeholder or error message
		// const errorSpan = document.createElement('span');
		// errorSpan.textContent = `Error loading image: ${alt || src}`;
		// errorSpan.className = 'text-red-500 text-xs italic block';
		// e.target.parentNode.appendChild(errorSpan);
		console.warn("Failed to load image:", src);
	};

	return (
		// Add figure/figcaption for better semantics? Optional.
		<span className="block my-6 lg:my-8">
			{" "}
			{/* Add vertical margin */}
			{/* Add lazy loading and decoding hint */}
			<img
				src={src}
				alt={alt || title || ""} // Ensure alt text is present
				title={title} // Use title attribute if provided
				loading="lazy"
				decoding="async"
				className="max-w-full h-auto rounded-lg shadow-md border border-gray-200 mx-auto" // Center images, add styling
				onError={handleError}
			/>
			{/* Optional: Render alt text as figcaption */}
			{alt && (
				<figcaption className="text-center text-xs text-gray-500 mt-2 italic">
					{alt}
				</figcaption>
			)}
		</span>
	);
};

// --- Custom Link Renderer ---
// Opens external links in new tab, uses icon
const CustomLinkRenderer = ({ href, children }) => {
	const isExternal =
		href &&
		(href.startsWith("http://") ||
			href.startsWith("https://") ||
			href.startsWith("mailto:") ||
			href.startsWith("tel:"));
	const isInternal = href && href.startsWith("/");

	if (isExternal) {
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer" // Security for target="_blank"
				className="inline-flex items-center gap-1 text-primary hover:underline break-words"
			>
				{children}
				<ExternalLink size={12} className="opacity-70" />
			</a>
		);
	}

	if (isInternal) {
		return (
			<Link to={href} className="text-primary hover:underline">
				{children}
			</Link>
		);
	}

	// Fallback for relative links or other protocols
	return (
		<a href={href} className="text-primary hover:underline">
			{children}
		</a>
	);
};

// --- Main Markdown Rendering Component ---
const MarkdownPost = ({ content }) => {
	if (!content) {
		return <p className="text-gray-500 italic">No content available.</p>;
	}

	return (
		// Apply Tailwind Typography prose classes for base styling
		// Customize prose modifiers as needed (e.g., prose-headings:font-bold)
		<div className="prose prose-lg prose-gray max-w-none prose-headings:font-semibold prose-a:text-primary hover:prose-a:underline prose-img:mt-0 prose-img:mb-0 prose-blockquote:border-primary/40 prose-blockquote:text-gray-600 prose-blockquote:not-italic">
			<ReactMarkdown
				// Source content
				children={content}
				// Custom Component Renderers
				components={{
					// Use custom renderer for images (and potentially video/audio if needed)
					img: CustomMediaRenderer,

					// Use custom renderer for links
					a: CustomLinkRenderer,

					// Optional: Customize other elements like blockquotes, headings, lists etc.
					// h1, h2, h3: Keep default prose heading styles unless specific changes needed
					blockquote: ({ children }) => (
						<blockquote className="border-l-4 border-primary/30 pl-4 py-1 my-4 italic text-gray-600 bg-primary/5 rounded-r-md">
							{children}
						</blockquote>
					),
					hr: () => <hr className="my-8 border-gray-200" />, // Style horizontal rules
				}}
			/>
		</div>
	);
};

export default MarkdownPost;
